import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AffiliateTermsPage = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <TermsContainer
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TermsHeader>
          <h1>{t('affiliate.terms.title')}</h1>
          <p>{t('affiliate.terms.lastUpdated')}: Mai 2025</p>
        </TermsHeader>
        
        <TermsContent>
          <Section>
            <h2>1. {t('affiliate.terms.sections.introduction.title')}</h2>
            <p>{t('affiliate.terms.sections.introduction.content1')}</p>
            <p>{t('affiliate.terms.sections.introduction.content2')}</p>
          </Section>
          
          <Section>
            <h2>2. {t('affiliate.terms.sections.definitions.title')}</h2>
            <DefinitionList>
              <DefinitionItem>
                <DefinitionTerm>{t('affiliate.terms.sections.definitions.term1')}</DefinitionTerm>
                <DefinitionDescription>
                  {t('affiliate.terms.sections.definitions.description1')}
                </DefinitionDescription>
              </DefinitionItem>
              
              <DefinitionItem>
                <DefinitionTerm>{t('affiliate.terms.sections.definitions.term2')}</DefinitionTerm>
                <DefinitionDescription>
                  {t('affiliate.terms.sections.definitions.description2')}
                </DefinitionDescription>
              </DefinitionItem>
              
              <DefinitionItem>
                <DefinitionTerm>{t('affiliate.terms.sections.definitions.term3')}</DefinitionTerm>
                <DefinitionDescription>
                  {t('affiliate.terms.sections.definitions.description3')}
                </DefinitionDescription>
              </DefinitionItem>
              
              <DefinitionItem>
                <DefinitionTerm>{t('affiliate.terms.sections.definitions.term4')}</DefinitionTerm>
                <DefinitionDescription>
                  {t('affiliate.terms.sections.definitions.description4')}
                </DefinitionDescription>
              </DefinitionItem>
              
              <DefinitionItem>
                <DefinitionTerm>{t('affiliate.terms.sections.definitions.term5')}</DefinitionTerm>
                <DefinitionDescription>
                  {t('affiliate.terms.sections.definitions.description5')}
                </DefinitionDescription>
              </DefinitionItem>
            </DefinitionList>
          </Section>
          
          <Section>
            <h2>3. {t('affiliate.terms.sections.enrollment.title')}</h2>
            <p>{t('affiliate.terms.sections.enrollment.content1')}</p>
            <p>{t('affiliate.terms.sections.enrollment.content2')}</p>
            <p>{t('affiliate.terms.sections.enrollment.content3')}</p>
          </Section>
          
          <Section>
            <h2>4. {t('affiliate.terms.sections.commissions.title')}</h2>
            <p>{t('affiliate.terms.sections.commissions.content1')}</p>
            <CommissionTable>
              <thead>
                <tr>
                  <th>{t('affiliate.terms.sections.commissions.tierLevel')}</th>
                  <th>{t('affiliate.terms.sections.commissions.requirement')}</th>
                  <th>{t('affiliate.terms.sections.commissions.rate')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('affiliate.terms.sections.commissions.standardTier')}</td>
                  <td>{t('affiliate.terms.sections.commissions.standardRequirement')}</td>
                  <td>$2 USD {t('affiliate.terms.sections.commissions.perConversion')}</td>
                </tr>
                <tr>
                  <td>{t('affiliate.terms.sections.commissions.premiumTier')}</td>
                  <td>{t('affiliate.terms.sections.commissions.premiumRequirement')}</td>
                  <td>$4 USD {t('affiliate.terms.sections.commissions.perConversion')}</td>
                </tr>
              </tbody>
            </CommissionTable>
            <p>{t('affiliate.terms.sections.commissions.content2')}</p>
            <p>{t('affiliate.terms.sections.commissions.content3')}</p>
          </Section>
          
          <Section>
            <h2>5. {t('affiliate.terms.sections.payments.title')}</h2>
            <p>{t('affiliate.terms.sections.payments.content1')}</p>
            <p>{t('affiliate.terms.sections.payments.content2')}</p>
            <p>{t('affiliate.terms.sections.payments.content3')}</p>
          </Section>
          
          <Section>
            <h2>6. {t('affiliate.terms.sections.referralLinks.title')}</h2>
            <p>{t('affiliate.terms.sections.referralLinks.content1')}</p>
            <p>{t('affiliate.terms.sections.referralLinks.content2')}</p>
            <p>{t('affiliate.terms.sections.referralLinks.content3')}</p>
          </Section>
          
          <Section>
            <h2>7. {t('affiliate.terms.sections.prohibitedActivities.title')}</h2>
            <p>{t('affiliate.terms.sections.prohibitedActivities.intro')}</p>
            <ProhibitedList>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item1')}
              </ProhibitedItem>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item2')}
              </ProhibitedItem>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item3')}
              </ProhibitedItem>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item4')}
              </ProhibitedItem>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item5')}
              </ProhibitedItem>
              <ProhibitedItem>
                {t('affiliate.terms.sections.prohibitedActivities.item6')}
              </ProhibitedItem>
            </ProhibitedList>
          </Section>
          
          <Section>
            <h2>8. {t('affiliate.terms.sections.termination.title')}</h2>
            <p>{t('affiliate.terms.sections.termination.content1')}</p>
            <p>{t('affiliate.terms.sections.termination.content2')}</p>
            <p>{t('affiliate.terms.sections.termination.content3')}</p>
          </Section>
          
          <Section>
            <h2>9. {t('affiliate.terms.sections.modifications.title')}</h2>
            <p>{t('affiliate.terms.sections.modifications.content1')}</p>
            <p>{t('affiliate.terms.sections.modifications.content2')}</p>
          </Section>
          
          <Section>
            <h2>10. {t('affiliate.terms.sections.miscellaneous.title')}</h2>
            <p>{t('affiliate.terms.sections.miscellaneous.content1')}</p>
            <p>{t('affiliate.terms.sections.miscellaneous.content2')}</p>
            <p>{t('affiliate.terms.sections.miscellaneous.content3')}</p>
          </Section>
        </TermsContent>
        
        <TermsFooter>
          <AcceptanceText>
            {t('affiliate.terms.acceptanceText')}
          </AcceptanceText>
          <ActionButtons>
            <BackLink to="/affiliate/signup">
              &larr; {t('affiliate.terms.backToSignup')}
            </BackLink>
          </ActionButtons>
        </TermsFooter>
      </TermsContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
`;

const TermsContainer = styled.div`
  max-width: 900px;
  width: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const TermsHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
  
  h1 {
    margin-bottom: 10px;
    color: #333;
  }
  
  p {
    color: #666;
    font-style: italic;
  }
`;

const TermsContent = styled.div`
  color: #333;
`;

const Section = styled.section`
  margin-bottom: 30px;
  
  h2 {
    font-size: 1.4rem;
    margin-bottom: 15px;
    color: #333;
  }
  
  p {
    margin-bottom: 15px;
    line-height: 1.6;
    color: #444;
  }
`;

const DefinitionList = styled.dl`
  margin-bottom: 20px;
`;

const DefinitionItem = styled.div`
  margin-bottom: 15px;
`;

const DefinitionTerm = styled.dt`
  font-weight: 600;
  margin-bottom: 5px;
`;

const DefinitionDescription = styled.dd`
  margin-left: 20px;
  line-height: 1.6;
  color: #444;
`;

const CommissionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const ProhibitedList = styled.ul`
  margin-left: 20px;
  margin-bottom: 20px;
`;

const ProhibitedItem = styled.li`
  margin-bottom: 10px;
  line-height: 1.6;
  color: #444;
`;

const TermsFooter = styled.div`
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const AcceptanceText = styled.p`
  font-style: italic;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
`;

const BackLink = styled(Link)`
  color: #6e8efb;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default AffiliateTermsPage;